import { useEffect, useRef, useState, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import { Camera, CameraOff, RefreshCw, SwitchCamera } from 'lucide-react';
import { Button, LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';

interface QRScannerProps {
  onScan: (decodedText: string) => void;
  isActive: boolean;
}

const QRScannerComponent: React.FC<QRScannerProps> = ({ onScan, isActive }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [cameras, setCameras] = useState<QrScanner.Camera[]>([]);
  const [activeCameraIdx, setActiveCameraIdx] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastScanRef = useRef<string>('');
  const lastScanTimeRef = useRef<number>(0);

  const handleScan = useCallback(
    (result: QrScanner.ScanResult) => {
      const now = Date.now();
      if (result.data === lastScanRef.current && now - lastScanTimeRef.current < 3000) {
        return;
      }
      lastScanRef.current = result.data;
      lastScanTimeRef.current = now;
      onScan(result.data);
    },
    [onScan],
  );

  const stopScanner = useCallback(() => {
    const scanner = scannerRef.current;
    if (scanner) {
      scanner.stop();
      setIsRunning(false);
    }
  }, []);

  const startScanner = useCallback(
    async (cameraId?: string) => {
      if (!videoRef.current) return;
      setIsStarting(true);
      setError(null);

      stopScanner();

      try {
        if (!scannerRef.current) {
          scannerRef.current = new QrScanner(videoRef.current, handleScan, {
            preferredCamera: cameraId || 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 10,
            onDecodeError: () => {},
          });
        } else if (cameraId) {
          await scannerRef.current.setCamera(cameraId);
        }

        await scannerRef.current.start();
        setIsRunning(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.toLowerCase().includes('permission')) {
          setError('Доступ к камере запрещён. Разрешите доступ в настройках браузера.');
        } else if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('no camera')) {
          setError('Камера не найдена. Подключите камеру и обновите страницу.');
        } else {
          setError(`Ошибка камеры: ${msg}`);
        }
      } finally {
        setIsStarting(false);
      }
    },
    [handleScan, stopScanner],
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const devices = await QrScanner.listCameras(true);
        if (cancelled) return;

        if (!devices.length) {
          setError('Камеры не обнаружены.');
          return;
        }

        setCameras(devices);

        const backIdx = devices.findIndex(
          (d) =>
            d.label.toLowerCase().includes('back') ||
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('задн'),
        );
        const idx = backIdx >= 0 ? backIdx : 0;
        setActiveCameraIdx(idx);

        if (isActive) {
          await startScanner(devices[idx].id);
        }
      } catch {
        if (!cancelled) {
          setError('Не удалось получить доступ к камерам. Проверьте разрешения.');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cameras.length) return;

    if (isActive && !isRunning && !isStarting) {
      startScanner(cameras[activeCameraIdx].id);
    } else if (!isActive && isRunning) {
      stopScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, []);

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const nextIdx = (activeCameraIdx + 1) % cameras.length;
    setActiveCameraIdx(nextIdx);

    if (scannerRef.current) {
      try {
        await scannerRef.current.setCamera(cameras[nextIdx].id);
      } catch {
        await startScanner(cameras[nextIdx].id);
      }
    }
  };

  const retryCamera = () => {
    setError(null);
    if (cameras.length) {
      startScanner(cameras[activeCameraIdx].id);
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="relative">
      {/* Camera viewport */}
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-2xl bg-gray-950',
          'aspect-[4/3] sm:aspect-video',
        )}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          muted
          playsInline
        />

        {/* Scan overlay — animated corners */}
        {isRunning && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-[250px] h-[250px]">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-[scanline_2s_ease-in-out_infinite]" />

              <div className="absolute top-0 left-0 w-8 h-8 border-t-[3px] border-l-[3px] border-emerald-400 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-[3px] border-r-[3px] border-emerald-400 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-[3px] border-l-[3px] border-emerald-400 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-[3px] border-r-[3px] border-emerald-400 rounded-br-lg" />
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isStarting && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/80 backdrop-blur-sm gap-3">
            <LoadingSpinner size="lg" />
            <span className="text-sm text-gray-300 font-medium">Запуск камеры...</span>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/90 backdrop-blur-sm gap-4 px-6 text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
              <CameraOff className="w-7 h-7 text-red-400" />
            </div>
            <p className="text-sm text-gray-300 max-w-xs">{error}</p>
            <Button variant="outline" size="sm" onClick={retryCamera}>
              <RefreshCw className="w-4 h-4" />
              Повторить
            </Button>
          </div>
        )}

        {/* Inactive overlay */}
        {!isActive && !error && !isStarting && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/80 backdrop-blur-sm gap-3">
            <Camera className="w-10 h-10 text-gray-400" />
            <span className="text-sm text-gray-400">Сканер на паузе</span>
          </div>
        )}
      </div>

      {/* Camera controls */}
      <div className="flex items-center justify-between mt-3 px-1">
        <span className="text-xs text-gray-500 truncate max-w-[60%]">
          {cameras.length > 0
            ? cameras[activeCameraIdx]?.label || `Камера ${activeCameraIdx + 1}`
            : 'Нет камеры'}
        </span>

        {cameras.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={switchCamera}
            disabled={isStarting}
            className="text-xs gap-1.5"
          >
            <SwitchCamera className="w-4 h-4" />
            Сменить
          </Button>
        )}
      </div>
    </div>
  );
};

export default QRScannerComponent;
